import React, { useState, useMemo, useEffect } from 'react';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, Input, FormGroup, Label } from 'reactstrap';
import { toast } from 'react-toastify';
import { chunk, uniq } from 'lodash';
import { useToggle } from 'react-use';

import firebase from '../../firebase';

const db = firebase.firestore();
const locationsRef = db.collection('locations');

export default function LocationsBatchAddModal({ isOpen, onClickClose, onFinish }) {
  const [text, setText] = useState('');
  const [isSubmitting, toggleSubmitting] = useToggle(false);
  const [existingKeysCache, setExistingKeysCache] = useState(null);
  const [isLoadingExisting, toggleLoadingExisting] = useToggle(false);

  const inputKeys = useMemo(() => {
    return text.split('\n').map(_ => _.trim()).filter(_ => _.length > 0);
  }, [text]);

  useEffect(() => {
    if (existingKeysCache !== null) return;
    if (inputKeys.length === 0) return;
    toggleLoadingExisting(true);
    (async () => {
      try {
        const chunks = chunk(uniq(inputKeys), 10);
        const results = await Promise.all(
          chunks.map(_ => locationsRef.where('sortKey', 'in', _).get())
        );
        const found = new Set(results.flatMap(_ => _.docs.map(d => d.data().sortKey)));
        setExistingKeysCache(found);
      } catch (e) {
        console.error(e);
        toast.error('既存ロケの確認に失敗しました');
      }
      toggleLoadingExisting(false);
    })();
  }, [inputKeys, existingKeysCache]);

  const onChangeText = ({ target: { value } }) => {
    setText(value);
    setExistingKeysCache(null);
  };

  const rows = useMemo(() => {
    const seen = new Map();
    return inputKeys.map((key, i) => {
      const dupInInput = seen.has(key);
      if (!dupInInput) seen.set(key, i);
      const dupInExisting = existingKeysCache?.has(key) ?? false;
      return { key, dupInInput, dupInExisting };
    });
  }, [inputKeys, existingKeysCache]);

  const hasDuplicates = rows.some(_ => _.dupInInput || _.dupInExisting);
  const isReady = existingKeysCache !== null && !isLoadingExisting;
  const isSubmittable = inputKeys.length > 0 && isReady && !hasDuplicates && !isSubmitting;

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!isSubmittable) return;
    toggleSubmitting(true);
    try {
      const now = new Date();
      await chunk(inputKeys, 500).reduce(async (x, keys) => {
        await x;
        const batch = db.batch();
        keys.forEach(key => batch.set(locationsRef.doc(), { sortKey: key, name: key, createdAt: now }));
        await batch.commit();
      }, Promise.resolve());
      toast.success(`${inputKeys.length}件のロケーションを追加しました`);
      setText('');
      setExistingKeysCache(null);
      onFinish && onFinish();
      onClickClose();
    } catch (e) {
      console.error(e);
      toast.error(e.message || '失敗しました');
    }
    toggleSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} size="lg">
      <ModalHeader>ロケーション一括追加</ModalHeader>
      <Form onSubmit={onSubmit}>
        <ModalBody>
          <FormGroup>
            <Label>ロケキー（1行1ロケ、改行区切り）</Label>
            <Input
              type="textarea"
              rows={10}
              value={text}
              onChange={onChangeText}
              placeholder={'A-01\nA-02\nA-03'}
              disabled={isSubmitting}
            />
            <div className="text-muted small mt-1">
              名称はキーと同じ値が自動的にセットされます
            </div>
          </FormGroup>
          {
            inputKeys.length > 0 && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <strong>プレビュー（{inputKeys.length}件）</strong>
                  {isLoadingExisting && <span className="text-muted small"><span className="fas fa-spin fa-spinner mr-1" />既存ロケを確認中</span>}
                </div>
                <div style={{ maxHeight: 300, overflowY: 'auto' }} className="border rounded p-2">
                  <table className="table table-sm mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: 60 }}>#</th>
                        <th>キー</th>
                        <th>状態</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        rows.map((row, i) => {
                          const isDup = row.dupInInput || row.dupInExisting;
                          return (
                            <tr key={i} className={isDup ? 'text-danger' : ''}>
                              <td>{i + 1}</td>
                              <td>{row.key}</td>
                              <td>
                                {row.dupInExisting && <span>既存と重複</span>}
                                {row.dupInInput && <span>{row.dupInExisting ? ' / ' : ''}入力内重複</span>}
                                {!isDup && isReady && <span className="text-success">新規</span>}
                              </td>
                            </tr>
                          );
                        })
                      }
                    </tbody>
                  </table>
                </div>
                {
                  hasDuplicates && (
                    <div className="text-danger small mt-2">
                      重複があるため保存できません。重複行を取り除いてください。
                    </div>
                  )
                }
              </div>
            )
          }
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onClickClose} disabled={isSubmitting}>閉じる</Button>
          <Button type="submit" color="primary" onClick={onSubmit} disabled={!isSubmittable}>
            {isSubmitting && <span className="fas fa-spin fa-spinner mr-1" />}
            {inputKeys.length > 0 ? `${inputKeys.length}件を追加` : '追加'}
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};
